import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useAuth from './hooks/useAuth';
import logo from '../../pe-logo.svg';
import './Login.css';
import {
    Card,
    CardContent,
    CardHeader,
    CardTitle,
} from '@/src/shadComponents/ui/card';
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from '@/src/shadComponents/ui/form';
import * as z from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Input } from '@/src/shadComponents/ui/input';
import { Separator } from '@/src/shadComponents/ui/separator';
import { Alert, AlertDescription } from '@/src/shadComponents/ui/alert';
import { AlertCircle } from 'lucide-react';
import { Heading } from '@/src/tailwindui/heading';

const formSchema = z.object({
    username: z.string().min(2).max(50),
    password: z.string().min(2).max(50),
});

export default function Login() {
    const navigate = useNavigate();
    const location = useLocation();
    const [feedback, setFeedback] = useState<string | null>(null);
    const { signin, persist } = useAuth();

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues: {
            username: '',
        },
    });

    // @ts-ignore
    let from = location.state?.from?.pathname || '/';

    async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();

        const formData = new FormData(event.currentTarget);
        const username = formData.get('username') as string;
        const password = formData.get('password') as string;

        try {
            const authenticated = await signin(
                username.toLowerCase(),
                password,
            );

            if (authenticated) {
                navigate(from, { replace: true });
            } else {
                setFeedback('Log in failed, try again.');
            }
        } catch (e) {
            setFeedback('Log in failed, try again.');
        }
    }

    useEffect(() => {
        // @ts-ignore
        localStorage.setItem('persist', persist);
    }, [persist]);

    return (
        <>
            <div className="flex items-center h-screen justify-center dark:bg-[#262C2C] dark:text-white">
                <Card className="w-[90%] md:min-w-2/6 md:w-2/6 rounded-none shadow-none border-0">
                    <CardHeader>
                        <CardTitle>
                            <Heading className="text-5xl w-full plex-sans">
                                Welcome back.
                            </Heading>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="mt-0">
                        <div className="Login-form relative flex flex-col gap-4">
                            <Form {...form}>
                                <form
                                    onSubmit={handleSubmit}
                                    className="w-full"
                                >
                                    <div
                                        id="buttonDiv"
                                        className="flex justify-center"
                                    ></div>
                                    <Separator className="my-4" />

                                    {feedback && (
                                        <Alert variant="destructive">
                                            <div className="flex items-center">
                                                <AlertCircle className="h-4 w-4" />
                                                <AlertDescription className="ml-2">
                                                    {feedback}
                                                </AlertDescription>
                                            </div>
                                        </Alert>
                                    )}
                                    <FormField
                                        control={form.control}
                                        name="username"
                                        render={({ field }) => (
                                            <FormItem className="my-3">
                                                <FormLabel className="plex-sans-bold text-xl">
                                                    Email Address
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        className="shadow-none monospace-font font-size-22"
                                                        type="text"
                                                    />
                                                </FormControl>

                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="password"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel className="plex-sans-bold text-xl">
                                                    Password
                                                </FormLabel>
                                                <FormControl>
                                                    <Input
                                                        {...field}
                                                        className="shadow-none monospace-font font-size-22"
                                                        type="password"
                                                    />
                                                </FormControl>

                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                    <br />

                                    <button
                                        type="submit"
                                        className="w-full black-button plex-sans-bold text-xl"
                                    >
                                        Continue
                                    </button>
                                </form>
                            </Form>
                            <div className="flex justify-start flex-row pt-2 w-full">
                                <p className="text-sm text-black/50 plex-sans whitespace-nowrap text-black dark:text-white">
                                    Not yet a Photo Engineer?
                                </p>
                                <p
                                    className="text-sm pl-4 z-2 text-black cursor-pointer underline plex-sans-bold dark:text-white whitespace-nowrap"
                                    onClick={() => {
                                        navigate('/signup');
                                    }}
                                >
                                    Create your free account
                                </p>
                                <div className="flex-1" />
                            </div>

                            <div className="flex justify-start flex-row w-full">
                                <p
                                    className="text-sm z-2 text-black cursor-pointer underline plex-sans-bold dark:text-white whitespace-nowrap"
                                    onClick={() => {
                                        navigate('/forgot-password');
                                    }}
                                >
                                    Forget your password?
                                </p>
                                <div className="flex-1" />
                            </div>
                        </div>
                        <div className="fixed bottom-4 right-4 ">
                            <img
                                className="w-[140px] clickable cursor-pointer object-contain pe-logo"
                                src={logo}
                                alt="PhotoEngineers"
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </>
    );
}
